import React from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import InputCst from "@components/InputCst/InputCst";
import SelectCst from "@components/InputCst/SelectCst";
import DataPickerCst from "@components/InputCst/DataPickerCst";
import MultiSelectCst from "@components/InputCst/MultiSelectCst";
import { DynamicFilterProps } from "./types";

const DynamicFilterCst: React.FC<DynamicFilterProps> = ({
  field,
  value,
  onChange,
  options,
  optionsKeyOverride,
}) => {
  const { i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "it";
  const label =
    field.headerLabels?.[lang] || field.headerLabels?.en || field.key;

  const radiusWrapperSx = {
    width: "100%",
    minWidth: 0,

    "& > *": {
      minWidth: "0 !important",
      width: "100% !important",
    },
    "& .MuiFormControl-root": {
      minWidth: "0 !important",
      width: "100% !important",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "8px",
    },
  };

  if (field.type === "multiselect") {
    const arrValue: string[] = Array.isArray(value) ? value : [];
    return (
      <Box sx={radiusWrapperSx}>
        <MultiSelectCst
          id={`filter-${field.key}`}
          label={label}
          value={arrValue}
          options={[]}
          optionsKey={field.optionsKey}
          service={field.service}
          optionsToDisable={
            field.optionsToDisable ? [field.optionsToDisable] : undefined
          }
          onChange={(next: any) => {
            const vals: string[] = (next || [])
              .map((o: any) => (typeof o === "string" ? o : o?.value))
              .filter(Boolean)
              .map((v: any) => v.toString().toUpperCase());
            const same =
              vals.length === arrValue.length &&
              vals.every((v, i) => v === arrValue[i]);
            if (!same) onChange(vals.length ? vals : null);
          }}
          isFilterActive={arrValue.length > 0}
          customWidth="100%"
        />
      </Box>
    );
  }

  if (field.type === "select") {
    return (
      <Box sx={radiusWrapperSx}>
        <SelectCst
          id={`filter-${field.key}`}
          label={label}
          value={(value as string) ?? ""}
          optionsKey={
            options ? undefined : (optionsKeyOverride ?? field.optionsKey)
          }
          options={options}
          onChange={(next: any) => onChange(next === "" ? null : next)}
          isFilterActive={!!value}
          customWidth="100%"
        />
      </Box>
    );
  }

  if (field.type === "date") {
    return (
      <Box sx={radiusWrapperSx}>
        <DataPickerCst
          id={`filter-${field.key}`}
          label={label}
          value={(value as string) ?? null}
          onChange={(next: any) => {
            if (!next) {
              onChange(null);
              return;
            }

            const date = new Date(next);
            const dateUTC = Date.UTC(
              date.getFullYear(),
              date.getMonth(),
              date.getDate(),
              0,
              0,
              0,
            );
            onChange(new Date(dateUTC).toISOString());
          }}
          isFilterActive={!!value}
          customWidth="100%"
        />
      </Box>
    );
  }

  return (
    <Box sx={radiusWrapperSx}>
      <InputCst
        id={`filter-${field.key}`}
        label={label}
        value={(value as string) ?? ""}
        onChange={(e: any) => onChange(e.target.value || null)}
        isFilterActive={!!value}
        customWidth="100%"
      />
    </Box>
  );
};

export default DynamicFilterCst;
